<template>
  <v-container>
    <v-card class="mx-auto rounded-xl">
      <v-row justify="center">
        <v-col cols="12">
          <v-card class="pa-4" elevation="4">
            <v-card-title>
              {{ isEditing ? 'Editar Modelo de Documento' : 'Novo Modelo de Documento' }}
            </v-card-title>
            <v-card-text>

              <v-tabs v-model="currentTab" dark class="mb-18">
                <v-tab value="details">Detalhes</v-tab>
              </v-tabs>

              <v-form ref="formRef" @submit.prevent="handleSubmit">
                <v-window v-model="currentTab" class="mt-2">
                  <v-window-item value="details">
                    <v-row class="mt-2">
                      <v-col cols="6">
                        <v-text-field v-model="formValue.title" label="Título"
                          :rules="[rules.required, rules.titleLength]"></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field v-model="formValue.description" label="Descrição (Opcional)"
                          maxlength="254"></v-text-field>
                      </v-col>

                      <v-col cols="12">
                        <v-file-input v-model="fileUpload" label="Arquivo do Documento (.doc, .docx, .pdf)"
                          :rules="[rules.requiredFile]" accept=".doc, .docx, .pdf" prepend-icon="mdi-file-document"
                          :clearable="!isEditing || !formValue.file_url" show-size :hint="fileHint"
                          persistent-hint></v-file-input>
                      </v-col>

                      <v-col cols="12" sm="6" md="3">
                        <v-switch v-model="formValue.is_active" color="success" label="Modelo Ativo?"></v-switch>
                      </v-col>
                    </v-row>
                  </v-window-item>
                </v-window>
              </v-form>
            </v-card-text>

            <v-card-actions class="d-flex justify-space-between">
              <v-btn color="secondary" variant="flat" @click="handleCancel">
                <v-icon left>mdi-arrow-left</v-icon>
                Voltar
              </v-btn>
              <v-btn color="primary" variant="flat" :loading="isSubmitting" :disabled="isSubmitting"
                @click="handleSubmit">
                <v-icon left>mdi-content-save</v-icon>
                {{ isEditing ? 'Salvar Alterações' : 'Cadastrar Modelo de Documento' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/services/api';
import { useToastStore } from '@/store/toast';
import { useLoadingOverlay } from '@/store/loadingOverlay';

const toastStore = useToastStore();
const loadingOverlay = useLoadingOverlay();
const route = useRoute();
const router = useRouter();
const formRef = ref(null);

const currentTab = ref('details');
const isSubmitting = ref(false);

const fileUpload = ref(null);

const formValue = ref({
  title: '',
  description: '',
  file_url: null,
  is_active: true,
});

const isEditing = computed(() => !!route.params.id);

const fileHint = computed(() => {
  if (isEditing.value && formValue.value.file_url && !fileUpload.value) {
    const fileName = formValue.value.file_url.substring(formValue.value.file_url.lastIndexOf('/') + 1);
    return `Arquivo atual: ${fileName}. Selecione um novo para substituir.`;
  }
  return 'Tamanho ilimitado. Formatos aceitos: .doc, .docx, .pdf';
});

const rules = {
  required: v => !!v || 'Campo obrigatório.',
  titleLength: v => (v && v.length <= 254) || 'O Título deve ter no máximo 254 caracteres.',
  requiredFile: v => {
    if (isEditing.value && formValue.value.file_url) return true;
    return !!v || 'O arquivo do documento é obrigatório.';
  }
};

// Carrega os dados existentes do modelo
const loadContractTemplates = async (id) => {
  loadingOverlay.startLoading();
  try {
    const response = await api.get(`/contract-templates/${id}`);
    const data = response.data.data;

    if (data) {
      formValue.value = {
        title: data.title,
        description: data.description,
        file_url: data.file_url, // Carrega a URL do arquivo
        is_active: data.is_active === 1,
      };
    } else {
      toastStore.showToast({ message: 'O modelo de documento não foi encontrado.', color: 'warning' });
    }

  } catch (error) {
    console.error('Erro ao carregar os dados do modelo de documento:', error);
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao carregar os dados do modelo de documento',
      color: 'error',
    });
  } finally {
    loadingOverlay.stopLoading();
  }
};

// Envia os dados e o arquivo
const handleSubmit = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) {
    currentTab.value = 'details';
    return;
  }

  isSubmitting.value = true;

  const formData = new FormData();

  formData.append('title', formValue.value.title);
  formData.append('description', formValue.value.description || '');
  formData.append('is_active', formValue.value.is_active ? 1 : 0);

  if (fileUpload.value) {
    formData.append('file', fileUpload.value);
  }

  if (isEditing.value) {
    formData.append('_method', 'PUT');
  }

  try {
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };

    if (isEditing.value) {
      await api.post(`/contract-templates/${route.params.id}`, formData, config);
    } else {
      await api.post('/contract-templates', formData, config);
    }

    router.push('/app/modelos-documento');
    toastStore.showToast({
      message: isEditing.value ? 'Modelo de documento atualizado com sucesso!' : 'Modelo de documento cadastrado com sucesso!',
      color: 'success',
    });

  } catch (error) {
    console.error('Erro ao salvar modelo de documento:', error);
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao salvar modelo de documento',
      color: 'error',
    });
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  router.push('/app/modelos-documento');
};

onMounted(() => {
  if (isEditing.value) {
    loadContractTemplates(route.params.id);
  }
});
</script>